import React, { useState, useEffect } from "react";
import SEO from "../seo";
const Domains = () => {
  const [activeSection, setActiveSection] = useState("overview");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "overview",
        "choose-your-domain-type",
        "free-xpage-subdomain",
        "custom-domain",
        "how-to-connect-custom-domain",
        "ssl-security",
        "test-your-connection",
        "linking-domains-to-landing-pages",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Domains | xPage Help Center"
        description="Learn how to set up and manage domains in xPage. Use a free xPage subdomain or connect your custom domain, configure DNS records, activate SSL security, verify your setup, and link domains to landing pages."
        keywords="xPage domains, connect domain, custom domain, xPage subdomain, DNS records, A record, CNAME, SSL certificate, domain verification, landing page domain"
        canonical="https://help.xpage.ai/help/settings/Domains"
        type="article"
      />

      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header id="overview" className="mb-6 scroll-mt-20">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Domains
          </h1>
          <p className="text-sm text-systemLabel">
            Connecting a domain in Xpage gives your store a professional look
            and builds customer trust. You can either use a free subdomain
            provided by Xpage or link a custom domain you already own for a
            fully branded experience.
          </p>
        </header>

        {/* Choose Your Domain Type Section */}
        <section id="choose-your-domain-type" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Choose Your Domain Type
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-6">
              {/* Free xPage Subdomain */}
              <div id="free-xpage-subdomain" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Free xPage Subdomain
                </h3>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Availability
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Launch your store instantly without buying a domain.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Example
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        myxpage.shop{" "}
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Use Case
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Ideal for testing or new stores starting out quickly.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Custom Domain */}
              <div id="custom-domain" className="scroll-mt-20">
                <h3 className="text-sm font-semibold text-systemTitle mb-2">
                  Custom Domain
                </h3>
                <table className="w-full mb-4">
                  <tbody className="border divide-y divide-systemBorder">
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Usage
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Use a domain you already own for a polished, branded
                        appearance.
                      </td>
                    </tr>
                    <tr className="">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Example
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        www.yourstore.com
                      </td>
                    </tr>
                    <tr className="bg-gray-400/10 transition-colors">
                      <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                        <strong className="text-systemTitle text-sm">
                          Benefits
                        </strong>
                      </td>
                      <td className="p-3 align-top text-systemLabel text-sm">
                        Perfect for establishing credibility and a professional
                        online presence.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        {/* How to Connect a Custom Domain Section */}
        <section
          id="how-to-connect-custom-domain"
          className="mb-6 scroll-mt-20"
        >
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            How to Connect a Custom Domain
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Go to Settings → Domain Settings in your xPage dashboard.
              </p>

              <p className="text-sm text-systemLabel">
                Enter your domain (e.g., www.yourstore.com).
              </p>

              <p className="text-sm text-systemLabel">
                Update your DNS records with your domain registrar:
              </p>

              <table className="w-full mb-4">
                <tbody className="border divide-y divide-systemBorder">
                  <tr className="bg-gray-400/10 transition-colors">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        A Record
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      Pointing to xPage's IP address (provided in the dashboard)
                      <br />
                      15.197.94.0 as an 'A' record
                      <br />
                      15.197.169.74 as an 'A' record
                    </td>
                  </tr>
                  <tr className="">
                    <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                      <strong className="text-systemTitle text-sm">
                        CNAME
                      </strong>
                    </td>
                    <td className="p-3 align-top text-systemLabel text-sm">
                      For subdomains if needed
                    </td>
                  </tr>
                </tbody>
              </table>

              <p className="text-sm text-systemLabel">
                DNS updates may take 24–48 hours to fully propagate. xPage will
                monitor your domain status and notify you when it's active.
              </p>
            </div>
          </div>
        </section>

        {/* SSL & Security Section */}
        <section id="ssl-security" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            SSL & Security
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                Once connected, Xpage automatically issues an SSL certificate
                for secure browsing (HTTPS).
              </p>

              <p className="text-sm text-systemLabel">
                No technical setup is required—you get secure, encrypted
                connections for your customers instantly.
              </p>
            </div>
          </div>
        </section>

        {/* Test Your Connection Section */}
        <section id="test-your-connection" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Test Your Connection
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                After updating DNS, click Verify Domain in Xpage.
              </p>

              <p className="text-sm text-systemLabel">
                Once verified, your store is live on your domain, ready for
                customers worldwide.
              </p>
            </div>
          </div>
        </section>

        {/* Linking Domains to Landing Pages Section */}
        <section id="linking-domains-to-landing-pages" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Linking Domains to Landing Pages
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="space-y-4">
              <p className="text-sm text-systemLabel">
                You can assign a specific domain or subdomain to any landing
                page you create in Xpage.
              </p>

              <p className="text-sm text-systemLabel">
                This ensures each landing page maintains consistent branding and
                a professional URL.
              </p>
            </div>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#overview"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "overview"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Overview
              </a>
            </li>
            <li>
              <a
                href="#choose-your-domain-type"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "choose-your-domain-type"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Choose Your Domain Type
              </a>
            </li>
            <li>
              <a
                href="#free-xpage-subdomain"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "free-xpage-subdomain"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Free xPage Subdomain
              </a>
            </li>
            <li>
              <a
                href="#custom-domain"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "custom-domain"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Custom Domain
              </a>
            </li>
            <li>
              <a
                href="#how-to-connect-custom-domain"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "how-to-connect-custom-domain"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                How to Connect
              </a>
            </li>
            <li>
              <a
                href="#ssl-security"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "ssl-security"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                SSL & Security
              </a>
            </li>
            <li>
              <a
                href="#test-your-connection"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "test-your-connection"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Test Your Connection
              </a>
            </li>
            <li>
              <a
                href="#linking-domains-to-landing-pages"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "linking-domains-to-landing-pages"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Linking to Landing Pages
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Domains;
